<template>
  <aside class="relative bg-slate-900 text-slate-100 flex flex-col h-screen select-none">
    <!-- LOGO HEADER -->
    <div class="px-6 py-4 flex items-center gap-3 text-lg font-semibold border-b border-slate-700 bg-[#D7642A]">
      <img src="../../assets/iwc_logo.png" alt="Logo" class="w-10 h-10 object-contain" />
      <p class="text-white truncate">IWC Gazette CMS</p>
    </div>

    <!-- MENU (scrollable) -->
    <div class="flex-1 overflow-y-auto px-0">
      <ul class="space-y-1">
        <!-- HOME BUTTON -->
        <li v-if="['super', 'creator', 'admin', 'coordinator'].includes(user.gazetteRole)">
          <router-link
            to="/home"
            class="group flex items-center gap-4 px-4 py-3 text-lg font-semibold cursor-pointer bg-blue-500/15 text-blue-500 hover:bg-blue-500/20 rounded-lg transition-colors"
            :class="{
              'ring-1 ring-blue-500/30': route.path === '/home',
            }"
            @click="$emit('sub-selected', { title: 'Home', description: 'Homepage of IWC - Gazette' })"
          >
            <!-- Home Icon -->
            <svg class="w-6 h-6 flex-shrink-0 text-blue-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3 9.75L12 3l9 6.75V21a.75.75 0 01-.75.75H15a.75.75 0 01-.75-.75v-4.5h-4.5V21a.75.75 0 01-.75.75H3.75A.75.75 0 013 21V9.75z"
              />
            </svg>

            <span class="truncate flex-1">Home</span>
          </router-link>
        </li>

        <!-- MODULES LOOP -->
        <li v-for="item in menuItems" :key="item.id">
          <!-- MAIN ITEM -->
          <div
            @click="toggleModule(item.id)"
            class="group flex items-center gap-3 px-3 py-2 text-base font-medium cursor-pointer text-gray-300 hover:bg-white hover:text-orange-500"
          >
            <span class="w-5 h-5 flex-shrink-0 flex items-center justify-center text-white group-hover:text-orange-500">
              <component :is="item.icon?.filename || 'tabLabel'" class="w-full h-full text-inherit" />
            </span>
            <span class="truncate flex-1 text-md font-medium">{{ item.name }}</span>
            <svg
              class="w-4 h-4 transition-transform duration-200"
              :class="{
                'rotate-90 text-orange-500': openModules[item.id],
                'text-gray-400': !openModules[item.id],
              }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </div>

          <!-- SUB ITEMS -->
          <transition name="collapse">
            <ul v-if="openModules[item.id] && Array.isArray(item.moduleSections) && item.moduleSections.length" class="ml-3 mt-1 space-y-1">
              <li v-for="sub in item.moduleSections" :key="sub.id">
                <router-link
                  :to="{
                    path: `/content/${item.code}/${sub.code}`,
                    query: { sectionId: sub.id },
                  }"
                  class="flex items-center gap-2 px-3 py-2 text-sm rounded text-gray-400 hover:bg-slate-700 hover:text-white"
                  :class="{
                    'bg-slate-700 text-white': route.path.includes(sub.code),
                  }"
                  @click="selectSub(sub)"
                >
                  <component :is="sub.icon?.filename || 'tabLabel'" height="21" width="21" />
                  {{ sub.name }}
                </router-link>
              </li>
            </ul>
          </transition>
        </li>
      </ul>
    </div>

    <!-- FOOTER -->
    <div class="mt-auto">
      <HeroSideBarFooter :user="user" @logout="tempLogout" />
    </div>
  </aside>
</template>

<script>
import { ref, onMounted, inject, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import tabLabel from "@/assets/svg/tabLabel.vue";
import HeroSideBarFooter from "@/components/common/HeroSideBarFooter.vue";

// ICONS

import MegaPhoneIcon from "@/components/svgs/icons/megaphone-icon.vue";
import ResponseIcon from "@/components/svgs/icons/response-icon.vue";
import ArchiveIcon from "@/components/svgs/icons/archive-icon.vue";
import DepartmentIcon from "@/components/svgs/icons/departmen-icon.vue";
import MemoIcon from "@/components/svgs/icons/memo-icon.vue";
import CertificateIcon from "@/components/svgs/icons/certificate-icon.vue";
import DonateHeartIcon from "@/components/svgs/icons/donateheart-icon.vue";
import CommunityIcon from "@/components/svgs/icons/community-icon.vue";
import LayerIcon from "@/components/svgs/icons/layer-icon.vue";
import FlagIcon from "@/components/svgs/icons/promotion-icon.vue";
import DocumentIcon from "@/components/svgs/icons/document-icon.vue";
import ShieldIcon from "@/components/svgs/icons/shield-icon.vue";
import BellIcon from "@/components/svgs/icons/bell-icon.vue";
import BuildingIcon from "@/components/svgs/icons/building-icon.vue";
import HeartIcon from "@/components/svgs/icons/heart-icon.vue";
import HierarchyIcon from "@/components/svgs/icons/hierarchy-icon.vue";
import CalendarIcon from "@/components/svgs/icons/calendar-icon.vue";
import CalendarCloseIcon from "@/components/svgs/icons/calendar-close-icon.vue";
import PartyIcon from "@/components/svgs/icons/party-icon.vue";

export default {
  emits: ["sub-selected"],
  components: {
    HeroSideBarFooter,
    tabLabel,
    "megaphone-icon": MegaPhoneIcon,
    "bell-icon": BellIcon,
    "building-icon": BuildingIcon,
    "heart-icon": HeartIcon,
    "hierarchy-icon": HierarchyIcon,
    "calendar-icon": CalendarIcon,
    "calendar-close-icon": CalendarCloseIcon,
    "party-icon": PartyIcon,
    "donateheart-icon": DonateHeartIcon,
    "layer-icon": LayerIcon,
    "shield-icon": ShieldIcon,
    "document-icon": DocumentIcon,
    "flag-icon": FlagIcon,
    "community-icon": CommunityIcon,
    "department-icon": DepartmentIcon,
    "memo-icon": MemoIcon,
    "certificate-icon": CertificateIcon,
    "response-icon": ResponseIcon,
    "archive-icon": ArchiveIcon,
  },
  setup(_, { emit }) {
    const store = useStore();
    const user = computed(() => store.state.auth.user);
    const axios = inject("$axios");
    const route = useRoute();
    const router = useRouter();

    const menuItems = ref([]);
    const openModules = ref({});

    const toggleModule = (id) => {
      openModules.value[id] = !openModules.value[id];
    };

    const selectSub = (sub) => {
      emit("sub-selected", { title: sub.name, description: sub.description });
    };

    const tempLogout = () => {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("userType");
      router.push("/");
    };

    onMounted(async () => {
      // 🔹 Persist Home title/description if at /home
      if (route.path === "/home") {
        emit("sub-selected", {
          title: "Home",
          description: "Homepage of IWC - Gazette",
        });
      }

      // fetch modules
      try {
        const { data } = await axios.get("/api/modules", {
          params: { includeSectionPosts: false, scope: "cms" },
        });
        menuItems.value = data.data || [];
        menuItems.value.forEach((item) => {
          openModules.value[item.id] = false;
        });

        // 🔹 Check route and pre-select correct module/sub
        const pathParts = route.path.split("/"); // e.g. ['', 'content', 'moduleCode', 'subCode']
        if (pathParts[1] === "content" && pathParts.length >= 4) {
          const moduleCode = pathParts[2];
          const subCode = pathParts[3];

          const moduleItem = menuItems.value.find((m) => m.code === moduleCode);
          if (moduleItem) {
            openModules.value[moduleItem.id] = true; // open the module

            const subItem = moduleItem.moduleSections?.find((s) => s.code === subCode);
            if (subItem) {
              emit("sub-selected", {
                title: subItem.name,
                description: subItem.description,
              });
            }
          }
        }
      } catch (error) {
        console.error("Sidebar menu fetch error:", error);
      }
    });

    return {
      menuItems,
      openModules,
      route,
      user,
      toggleModule,
      selectSub,
      tempLogout,
    };
  },
};
</script>

<style scoped>
.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.2s ease;
}

.collapse-enter-from,
.collapse-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
